import { useEffect, useRef } from 'react';
import { useSize } from 'ahooks';
import { initEcharts, applyChartTheme } from 'lib/echarts';
import { Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import arrowSvg from 'views/Dashboard/assets/arrow-down.svg';
import dayjs from 'lib/dayjs';
import { numDisplay, percentDisplay, sortByDate } from 'views/Dashboard/ImplementData/utils';
import Empty from 'components/Empty';

export interface Summary {
  top: {
    title: string;
    tooltip?: string;
    value: string;
  };
  bottom: {
    title: string;
    tooltip?: string;
    value: string;
  };
}
export interface Options {
  bar: {
    name: string;
    dataKey: string;
  }[];
  line: {
    name: string;
    dataKey: string;
  }[];
}
interface Props {
  options?: Options;
  summary: Summary;
  data?: any[];
}
const Container = styled.div`
  display: flex;
`;

const Left = styled.div`
  width: 200px;
  position: relative;
  padding-top: 34px;
  .card {
    position: relative;
    min-height: 88px;
    border: 1px solid rgba(162, 186, 255, 1);
    display: flex;
    border-radius: 4px;
    padding: 0 16px;
    justify-content: center;
    flex-direction: column;
    align-items: flex-start;
    background: var(--fett-scenario-accent-bg);
    .title {
      color: var(--fett-text-tertiary);
    }
    .num {
      color: var(--fett-text-secondary);
      font-size: 20px;
    }
  }
  .card + .card {
    margin-top: 12px;
  }
  img {
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translate(-50%, 50%);
  }
  svg {
    margin-left: 4px;
  }
`;
const Relative = styled.div`
  position: relative;
  width: 100%;
  min-height: 340px;
  .ant-empty {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
  .bar {
    width: 100%;
    height: 340px;
  }
`;

const barColors = ['#5B8FF9', '#5AD8A6'];
const lineColors = ['#f6903d'];

export const getOptions = (
  value?: any[],
  config?: {
    bar: {
      name: string;
      dataKey: string;
    }[];
    line: {
      name: string;
      dataKey: string;
    }[];
  },
) => {
  const data = sortByDate(value) || [];
  const dateRange = data.map((x) => x.date).map((x) => dayjs(x).format('MM-DD'));
  const { bar = [], line = [] } = config || {};
  const dataZoom = [
    {
      realtime: true,
      start: 0,
      end: 100,
      show: dateRange.length > 10,
    },
    {
      type: 'inside',
      realtime: true,
      start: 0,
      end: 100,
      show: dateRange.length > 10,
    },
  ];
  return {
    dataZoom,
    legend: {
      data: [...bar, ...line].map((x) => x.name),
    },
    grid: {
      left: '6%',
      right: '6%',
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: dateRange,
    },
    yAxis: [
      {
        type: 'value',
        name: '',
      },
      {
        type: 'value',
        name: '%',
        nameTextStyle: {
          align: 'left',
        },
      },
    ],
    tooltip: {
      trigger: 'axis',
    },
    series: [
      ...bar.map((x, index) => {
        return {
          data: data.map((item) => item[x.dataKey]),
          type: 'bar',
          name: x.name,
          tooltip: {
            valueFormatter: (value: number) => `${numDisplay(Number(value))}`,
          },
          itemStyle: {
            color: barColors[index],
            opacity: 0.85,
          },
        };
      }),
      ...line.map((x, index) => {
        return {
          data: data.map((item) => item[x.dataKey] * 100),
          tooltip: {
            valueFormatter: (value: number) => `${percentDisplay(Number(value) / 100)}`,
          },
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          name: x.name,
          itemStyle: {
            color: lineColors[index],
          },
          lineStyle: {
            color: lineColors[index],
          },
        };
      }),
    ],
  } as const;
};

const RobotContributionChart = (props: Props) => {
  const { options, summary, data } = props;

  const chartDomRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts>();
  const { width } = useSize(chartDomRef) || {};

  useEffect(() => {
    if (!options || !data || !chartDomRef.current) {
      return;
    }
    if (!chartRef.current) {
      chartRef.current = initEcharts(chartDomRef.current);
    }
    const chartIns = chartRef.current;
    chartIns.setOption(applyChartTheme(getOptions(data, options)));
  }, [options, data]);
  useEffect(() => {
    chartRef.current?.resize();
  }, [width]);

  return (
    <Container>
      <Left>
        <div className="card">
          <div className="title">
            {summary.top.title}
            {summary.top.tooltip ? (
              <Tooltip title={summary.top.tooltip}>
                <QuestionCircleOutlined />
              </Tooltip>
            ) : null}
          </div>
          <div className="num">{summary.top.value}</div>
          <img src={arrowSvg} alt="bg" />
        </div>
        <div className="card">
          <div className="title">
            {summary.bottom.title}
            {summary.bottom.tooltip ? (
              <Tooltip title={summary.bottom.tooltip}>
                <QuestionCircleOutlined />
              </Tooltip>
            ) : null}
          </div>
          <div className="num">{summary.bottom.value}</div>
        </div>
      </Left>
      <Relative>
        {!data?.length ? <Empty /> : null}

        <div ref={chartDomRef} className="bar" />
      </Relative>
    </Container>
  );
};

export default RobotContributionChart;
